import React, {FC, useState} from "react";
import {useTranslation} from "react-i18next";
import * as Yup from "yup";
import classNames from "classnames";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {Button, Popup} from "../../components";
import {ScanModel} from "../../resources/models";
import {ScansService, ToastService} from "../../services";

export interface ISharePhotosPopupProps {
  className?: string;
  scan: ScanModel;
  onClose(): void;
}

export const SharePhotosPopup: FC<ISharePhotosPopupProps> = ({
  className,
  scan,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const formSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('validationErrors.invalidEmail'))
      .required(t('validationErrors.requiredField')),
  });

  const initialFormValues = {
    email: '',
  };

  const onSubmit = (form) => {
    setLoading(true);
    ScansService.shareScan(scan.id, form.email, false)
      .then(() => {
        ToastService.success('toast.photoShared');
        onClose();
      })
      .catch((err) => ToastService.showHttpError(err, 'toast.photoSharingFailed'))
      .finally(() => setLoading(false));
  };

  return (
    <Popup
      contentClass={classNames('text-typo1', className)}
      width="21rem"
      onClose={onClose}
    >
      <Formik
        initialValues={initialFormValues}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {(formik) => {
          const { errors, touched } = formik;
          return (
            <Form className="formik-form">
              <h1>{t('patient.sharePhotos')}</h1>
              <p className="mt-1">{t('patient.sharePhotosHelperText')}.</p>

              <div className={classNames(
                'form-field full-width rounded-sm size-md mt-2',
                errors.email && touched.email && 'is-invalid',
              )}>
                <Field
                  name="email"
                  placeholder={t('common.email')}
                />
                <ErrorMessage name="email" component="div" className="invalid-feedback mt-1 ml-2" />
              </div>

              <Button className="mt-4" type="submit" fullWidth loading={loading}>
                {t('common.share')}
              </Button>
              <Button className="mt-4" type="button" variant="outlined" fullWidth onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Popup>
  );
};
